/**
 * Path Calculator Utility
 * Calculates curved SVG paths between stat card elements
 * for the circuit board aesthetic
 */

export interface Connection {
    path: string;
    length: number;
    from: number;
    to: number;
}

export interface Position {
    x: number;
    y: number;
    width: number;
    height: number;
}

/**
 * Calculate positions of elements relative to a container
 */
export function getElementPositions(
    elements: Element[],
    container: Element
): Position[] {
    const containerRect = container.getBoundingClientRect();

    return elements.map(el => {
        const rect = el.getBoundingClientRect();
        return {
            x: rect.left - containerRect.left + rect.width / 2,
            y: rect.top - containerRect.top + rect.height / 2,
            width: rect.width,
            height: rect.height,
        };
    });
}

/**
 * Create a curved quadratic bezier path between two points
 */
function createCurvedPath(
    start: Position,
    end: Position
): { path: string; length: number } {
    const dx = end.x - start.x;
    const dy = end.y - start.y;

    // Control point for curve (arc upward for horizontal connections)
    const controlX = start.x + dx / 2;
    const controlY = Math.min(start.y, end.y) - Math.abs(dx) * 0.15; // Arc height based on distance

    const path = `M ${start.x} ${start.y} Q ${controlX} ${controlY} ${end.x} ${end.y}`;

    // Approximate path length
    const length = Math.sqrt(dx * dx + dy * dy) * 1.2; // Rough estimate for curved path

    return { path, length };
}

/**
 * Calculate SVG connection paths between stat cards
 * Pattern: Card 1→2, 2→4, 1→3, 3→4
 */
export function calculateConnectionPaths(
    elements: Element[],
    container: Element
): Connection[] {
    if (elements.length < 4) {
        console.warn('Need at least 4 elements for circuit board connections');
        return [];
    }

    const positions = getElementPositions(Array.from(elements), container);
    const connections: Connection[] = [];

    // Define connection pattern (0-indexed)
    const pattern = [
        [0, 1], // Card 1 → Card 2
        [1, 3], // Card 2 → Card 4
        [0, 2], // Card 1 → Card 3
        [2, 3], // Card 3 → Card 4
    ];

    pattern.forEach(([from, to]) => {
        if (positions[from] && positions[to]) {
            const { path, length } = createCurvedPath(positions[from], positions[to]);
            connections.push({ path, length, from, to });
        }
    });

    return connections;
}

/**
 * Get path length for stroke-dasharray/dashoffset animation
 */
export function getPathLength(pathElement: SVGPathElement): number {
    return pathElement.getTotalLength();
}

/**
 * Debounced resize handler for recalculating paths
 */
export function createResizeHandler(
    callback: () => void,
    delay: number = 250
): () => void {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    return () => {
        if (timeoutId) clearTimeout(timeoutId);
        timeoutId = setTimeout(callback, delay);
    };
}
